import { noop } from '@chillibean/component-library';

import { Site, StudyProtocol } from '../../models';
import {
  createStudyArm,
  createStudyEventDefinition,
  createStudyEventProcedure,
  createStudyProcedureDefinition,
  createStudyProcedureRoleDefinition,
  createStudyProcedureTaskDefinition,
  createStudyProtocol,
  deleteStudyProtocol,
  deleteStudyArm,
  deleteStudyEventDefinition,
  deleteStudyEventProcedure,
  deleteStudyProcedureDefinition,
  deleteStudyProcedureRoleDefinition,
  deleteStudyProcedureTaskDefinition,
  duplicateStudyProtocol,
  getStudyProtocol,
  getStudyProtocols,
  updateStudyArm,
  updateStudyEventDefinition,
  updateStudyProcedureDefinition,
  updateStudyProcedureRoleDefinition,
  updateStudyProcedureTaskDefinition,
  updateStudyProtocol,
  updateStudyProtocolSites,
} from '../../services';
import { isFieldError } from '../../utils';
import { setSitesData } from './sitesActions';
import * as actionTypes from './types';

// Redux Actions
export const setProtocols = (value) => ({
  type: actionTypes.SET_PROTOCOLS,
  value: value,
});

export const setProtocol = (value) => ({
  type: actionTypes.SET_PROTOCOL,
  value: value,
});

export const setProtocolAction = (value) => ({
  type: actionTypes.SET_PROTOCOL_ACTION,
  value: value,
});

export const setProtocolStudyEventProcedure = (value) => ({
  type: actionTypes.SET_PROTOCOL_STUDY_EVENT_PROCEDURE,
  value: value,
});

export const setStudyProtocolSitesAssignmentData = (value) => ({
  type: actionTypes.SET_STUDY_PROTOCOL_SITES_ASSIGNMENT_DATA,
  value: value,
});

export const setProtocolUpdated = (value) => ({
  type: actionTypes.SET_PROTOCOL_UPDATED,
  value: value,
});

// StudyProtocol Actions
export const fetchProtocols = () => async (dispatch, getState) => {
  const { account, authenticityToken } = getState().internalReducer.internal;
  dispatch(setProtocols({ data: [], error: false, loading: true }));

  try {
    const data = await getStudyProtocols({
      accountId: account.id,
      authenticityToken: authenticityToken,
    });

    dispatch(
      setProtocols({
        data: data.map((studyProtocol) => new StudyProtocol(studyProtocol)),
        error: false,
        loading: false,
      }),
    );
  } catch (error) {
    dispatch(setProtocols({ data: [], error: error, loading: false }));
  }
};

export const fetchProtocol = (id) => async (dispatch, getState) => {
  const { account, authenticityToken } = getState().internalReducer.internal;
  dispatch(setProtocol({ data: undefined, error: false, loading: true }));

  try {
    const data = await getStudyProtocol({ accountId: account.id, authenticityToken: authenticityToken, id: id });

    dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
  } catch (error) {
    dispatch(setProtocol({ data: undefined, error: error, loading: false }));
  }
};

export const createProtocol =
  ({ accountId, authenticityToken, onError = noop, onSuccess = noop, protocolData }) =>
  async (dispatch) => {
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await createStudyProtocol({
        accountId: accountId,
        authenticityToken: authenticityToken,
        data: protocolData,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess(data);
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const updateProtocol =
  ({ identifier, onError = noop, onSuccess = noop, protocolData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await updateStudyProtocol({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: protocolData,
        identifier: identifier,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const assignStudyProtocolSites =
  ({ data, identifier, onError = noop, onSuccess = noop }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setStudyProtocolSitesAssignmentData({ error: null, loading: true }));

    try {
      const resData = await updateStudyProtocolSites({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: data,
        identifier: identifier,
      });

      dispatch(setSitesData({ data: resData.map((site) => new Site(site)), error: null, loading: false }));
      dispatch(setStudyProtocolSitesAssignmentData({ error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setStudyProtocolSitesAssignmentData({ error: error, loading: false }));
      onError(error);
    }
  };

export const duplicateProtocol =
  ({ identifier, onError = noop, onSuccess = noop }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await duplicateStudyProtocol({
        accountId: account.id,
        authenticityToken: authenticityToken,
        identifier: identifier,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess(data);
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const deleteProtocol =
  ({ identifier, onError = noop, onSuccess = noop, reasonForDeleting }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await deleteStudyProtocol({
        accountId: account.id,
        authenticityToken: authenticityToken,
        identifier: identifier,
        reasonForDeleting: reasonForDeleting,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess(data);
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

// Study Event Definition Actions
export const setStudyEventDefinitionFilteredByStudyArm = (value) => {
  return {
    type: actionTypes.SET_STUDY_EVENT_DEFINITION_FILTERED_BY_STUDY_ARM,
    value: value,
  };
};

export const addStudyEventDefinition =
  ({ onError = noop, onSuccess = noop, protocolId, studyEventDefinitionData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await createStudyEventDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: studyEventDefinitionData,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const modifyStudyEventDefinition =
  ({ id, onError = noop, onSuccess = noop, protocolId, studyEventDefinitionData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await updateStudyEventDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: studyEventDefinitionData,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const removeStudyEventDefinition =
  ({ id, onError = noop, onSuccess = noop, protocolId }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await deleteStudyEventDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      onError(error);
    }
  };

// Study Procedure Definition Actions
export const addStudyProcedureDefinition =
  ({ onError = noop, onSuccess = noop, protocolId, studyProcedureDefinitionData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await createStudyProcedureDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: studyProcedureDefinitionData,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const modifyStudyProcedureDefinition =
  ({ id, onError = noop, onSuccess = noop, protocolId, studyProcedureDefinitionData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await updateStudyProcedureDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: studyProcedureDefinitionData,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const removeStudyProcedureDefinition =
  ({ id, onError = noop, onSuccess = noop, protocolId }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await deleteStudyProcedureDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        identifier: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      onError(error);
    }
  };

// Study Event Procedure Actions
export const addStudyEventProcedure =
  ({ onError = noop, onSuccess = noop, protocolId, studyEventProcedureData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await createStudyEventProcedure({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: {
          studyEventDefinitionId: studyEventProcedureData.studyEventDefinitionId,
          studyProcedureDefinitionId: studyEventProcedureData.studyProcedureDefinitionId,
        },
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data?.data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      onError(error);
    }
  };

export const removeStudyEventProcedure =
  ({ onError = noop, onSuccess = noop, protocolId, studyEventProcedureData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await deleteStudyEventProcedure({
        accountId: account.id,
        authenticityToken: authenticityToken,
        protocolId: protocolId,
        studyEventProcedureId: studyEventProcedureData.studyEventProcedureId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data?.data), error: false, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      onError(error);
    }
  };

// Study Arm Actions
export const addStudyArm =
  ({ onError = noop, onSuccess = noop, protocolId, studyArmData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await createStudyArm({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: studyArmData,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const modifyStudyArm =
  ({ id, onError = noop, onSuccess = noop, protocolId, studyArmData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await updateStudyArm({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: studyArmData,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const removeStudyArm =
  ({ id, onError = noop, onSuccess = noop, protocolId }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await deleteStudyArm({
        accountId: account.id,
        authenticityToken: authenticityToken,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      onError(error);
    }
  };

// Study Procedure Task Definitions Actions
export const addStudyProcedureTaskDefinition =
  ({ onError = noop, onSuccess = noop, protocolId, studyProcedureTaskDefinitionData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await createStudyProcedureTaskDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: studyProcedureTaskDefinitionData,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const modifyStudyProcedureTaskDefinition =
  ({ id, onError = noop, onSuccess = noop, protocolId, studyProcedureTaskDefinitionData }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await updateStudyProcedureTaskDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: studyProcedureTaskDefinitionData,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const removeStudyProcedureTaskDefinition =
  ({ id, onError = noop, onSuccess = noop, protocolId }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await deleteStudyProcedureTaskDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      onError(error);
    }
  };

// Study Procedure Role Definitions Actions
export const addStudyProcedureRoleDefinition =
  ({ contributorRoleData, onError = noop, onSuccess = noop, protocolId }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await createStudyProcedureRoleDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: contributorRoleData,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const modifyStudyProcedureRoleDefinition =
  ({ contributorRoleData, id, onError = noop, onSuccess = noop, protocolId }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await updateStudyProcedureRoleDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        data: contributorRoleData,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      if (!isFieldError(error)) onError(error);
    }
  };

export const removeStudyProcedureRoleDefinition =
  ({ id, onError = noop, onSuccess = noop, protocolId }) =>
  async (dispatch, getState) => {
    const { account, authenticityToken } = getState().internalReducer.internal;
    dispatch(setProtocolAction({ error: null, loading: true }));

    try {
      const data = await deleteStudyProcedureRoleDefinition({
        accountId: account.id,
        authenticityToken: authenticityToken,
        id: id,
        protocolId: protocolId,
      });

      dispatch(setProtocolAction({ error: null, loading: false }));
      dispatch(setProtocol({ data: new StudyProtocol(data), error: null, loading: false }));
      onSuccess();
    } catch (error) {
      dispatch(setProtocolAction({ error: error, loading: false }));
      onError(error);
    }
  };
